import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, DollarSign, FileText, TrendingUp, UserCheck, UserX, LogOut, Search, Edit, Mail, ShieldOff, RefreshCcw, ArrowLeft, Trash2 } from 'lucide-react';
import axios from 'axios';
import { useAdmin } from '../contexts/AdminContext';
import { useAuth } from '../contexts/AuthContext';
import Notification from './Notification';
import ConfirmModal from './ConfirmModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const currencyFormatter = new Intl.NumberFormat('en-CA', {
  style: 'currency',
  currency: 'CAD',
  maximumFractionDigits: 2
});

const formatCurrency = (value) => currencyFormatter.format(Number(value) || 0);
const formatDate = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleDateString();
};

const getAdminAuthHeaders = () => {
  const token = localStorage.getItem('admin_session_token') || localStorage.getItem('session_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { admin, logout } = useAdmin();
  const { logout: userLogout } = useAuth();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [editingUser, setEditingUser] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', email: '' });
  const [expandedUserId, setExpandedUserId] = useState(null);
  const [inviteEmail, setInviteEmail] = useState('');
  const [sendingInvite, setSendingInvite] = useState(false);
  const [notification, setNotification] = useState(null);
  const [confirmModal, setConfirmModal] = useState(null);

  const showNotification = (type, message) => {
    setNotification({ type, message });
    setTimeout(() => setNotification(null), 5000);
  };

  const showConfirm = (config) => {
    return new Promise((resolve) => {
      setConfirmModal({
        ...config,
        onConfirm: () => {
          setConfirmModal(null);
          resolve(true);
        },
        onCancel: () => {
          setConfirmModal(null);
          resolve(false);
        }
      });
    });
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadData = async () => {
    try {
      const [statsRes, usersRes, invitesRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, {
          headers: getAdminAuthHeaders()
        }),
        axios.get(`${API}/admin/users`, {
          headers: getAdminAuthHeaders()
        }),
        axios.get(`${API}/admin/invites`, {
          headers: getAdminAuthHeaders()
        })
      ]);
      
      setStats(statsRes.data);
      setUsers(usersRes.data);
      setInvites(invitesRes.data);
    } catch (error) {
      console.error('Error loading admin data:', error);
      if (error.response?.status === 401) {
        await logout();
        navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSendInvite = async (event) => {
    event.preventDefault();
    const email = inviteEmail.trim().toLowerCase();
    if (!email) {
      showNotification('error', 'Enter an email to invite.');
      return;
    }

    setSendingInvite(true);
    try {
      await axios.post(`${API}/admin/invites`, { email }, { headers: getAdminAuthHeaders() });
      setInviteEmail('');
      await loadData();
      showNotification('success', `Invite sent to ${email}`);
    } catch (error) {
      console.error('Error sending invite:', error);
      showNotification('error', error.response?.data?.detail || 'Failed to send invite');
    } finally {
      setSendingInvite(false);
    }
  };

  const handleCopyInviteLink = async (link) => {
    try {
      await navigator.clipboard.writeText(link);
      showNotification('success', 'Invite link copied');
    } catch (error) {
      console.error('Failed to copy invite link:', error);
      showNotification('error', 'Failed to copy invite link');
    }
  };

  const handleDeleteInvite = async (invite) => {
    const confirmed = await showConfirm({
      title: 'Delete invite?',
      message: `Delete invite for ${invite.email}? This cannot be undone.`,
      type: 'danger'
    });
    if (!confirmed) return;

    try {
      await axios.delete(`${API}/admin/invites/${invite.invite_id}`, {
        headers: getAdminAuthHeaders()
      });
      setInvites((prev) => prev.filter((item) => item.invite_id !== invite.invite_id));
      showNotification('success', 'Invite deleted');
    } catch (error) {
      console.error('Failed to delete invite:', error);
      showNotification('error', error.response?.data?.detail || 'Failed to delete invite');
    }
  };

  const handleToggleSubscription = async (userId, currentStatus) => {
    const newStatus = currentStatus === 'active' ? 'inactive' : 'active';
    
    const confirmed = await showConfirm({
      title: `${newStatus === 'active' ? 'Activate' : 'Deactivate'} Subscription?`,
      message: `Are you sure you want to ${newStatus === 'active' ? 'activate' : 'deactivate'} this user's subscription?`,
      type: newStatus === 'inactive' ? 'danger' : 'warning'
    });

    if (!confirmed) return;

    try {
      await axios.put(`${API}/admin/users/${userId}/subscription`, 
        { subscription_status: newStatus },
        { headers: getAdminAuthHeaders() }
      );
      
      await loadData();
      showNotification('success', 'Subscription updated successfully!');
    } catch (error) {
      console.error('Error updating subscription:', error);
      showNotification('error', 'Failed to update subscription. Please try again.');
    }
  };

  const handleEditUser = (user) => {
    setEditForm({ name: user.name || '', email: user.email || '' });
    setEditingUser(user);
  };

  const handleUpdateUser = async () => {
    const confirmed = await showConfirm({
      title: 'Save changes?',
      message: 'This will update the user profile information.',
      type: 'warning',
      confirmText: 'Save'
    });

    if (!confirmed) return;

    try {
      await axios.put(
        `${API}/admin/users/${editingUser.user_id}`,
        { name: editForm.name, email: editForm.email },
        { headers: getAdminAuthHeaders() }
      );
      setEditingUser(null);
      await loadData();
      showNotification('success', 'User updated successfully');
    } catch (error) {
      console.error('Error updating user:', error);
      showNotification('error', error.response?.data?.detail || 'Failed to update user');
    }
  };

  const handleToggleAccountStatus = async (user) => {
    const newStatus = user.account_status === 'active' ? 'disabled' : 'active';
    const confirmed = await showConfirm({
      title: `${newStatus === 'active' ? 'Enable' : 'Disable'} Account?`,
      message: `Are you sure you want to ${newStatus === 'active' ? 'enable' : 'disable'} this account?`,
      type: newStatus === 'disabled' ? 'danger' : 'warning'
    });

    if (!confirmed) return;

    try {
      await axios.put(
        `${API}/admin/users/${user.user_id}/account-status`,
        { account_status: newStatus },
        { headers: getAdminAuthHeaders() }
      );
      await loadData();
      showNotification('success', 'Account status updated');
    } catch (error) {
      console.error('Error updating account status:', error);
      showNotification('error', 'Failed to update account status');
    }
  };

  const handleSendResetEmail = async (user) => {
    const confirmed = await showConfirm({
      title: 'Send password reset?',
      message: `Send a password reset email to ${user.email}?`,
      type: 'warning',
      confirmText: 'Send'
    });

    if (!confirmed) return;

    try {
      await axios.post(
        `${API}/admin/users/${user.user_id}/reset-password-email`,
        {},
        { headers: getAdminAuthHeaders() }
      );
      showNotification('success', 'Reset email sent');
    } catch (error) {
      console.error('Error sending reset email:', error);
      showNotification('error', error.response?.data?.detail || 'Failed to send reset email');
    }
  };

  const handleRevokeSessions = async (user) => {
    const confirmed = await showConfirm({
      title: 'Revoke sessions?',
      message: 'This will force the user to log in again on all devices.',
      type: 'warning',
      confirmText: 'Revoke'
    });

    if (!confirmed) return;

    try {
      await axios.post(
        `${API}/admin/users/${user.user_id}/revoke-sessions`,
        {},
        { headers: getAdminAuthHeaders() }
      );
      showNotification('success', 'Sessions revoked');
    } catch (error) {
      console.error('Error revoking sessions:', error);
      showNotification('error', 'Failed to revoke sessions');
    }
  };

  const handleCancelSubscription = async (user) => {
    const confirmed = await showConfirm({
      title: 'Cancel subscription?',
      message: 'This will cancel the Stripe subscription and mark the account inactive.',
      type: 'danger',
      confirmText: 'Cancel Subscription',
      confirmColor: '#dc3545'
    });

    if (!confirmed) return;

    try {
      await axios.post(
        `${API}/admin/users/${user.user_id}/cancel-subscription`,
        {},
        { headers: getAdminAuthHeaders() }
      );
      await loadData();
      showNotification('success', 'Subscription canceled');
    } catch (error) {
      console.error('Error canceling subscription:', error);
      showNotification('error', error.response?.data?.detail || 'Failed to cancel subscription');
    }
  };

  const handleDeleteUser = async (userId, userName) => {
    const confirmed = await showConfirm({
      title: 'Delete User?',
      message: `Are you sure you want to DELETE user "${userName}"? This will delete all their data including quotes. This action cannot be undone!`,
      type: 'danger'
    });

    if (!confirmed) return;

    try {
      await axios.delete(`${API}/admin/users/${userId}`, {
        headers: getAdminAuthHeaders()
      });
      
      await loadData();
      showNotification('success', 'User deleted successfully');
    } catch (error) {
      console.error('Error deleting user:', error);
      showNotification('error', 'Failed to delete user');
    }
  };

  const handleLogout = async () => {
    await logout();
    await userLogout();
    navigate('/login');
  };

  const handleBackToApp = () => {
    navigate('/');
  };

  const filteredUsers = users.filter(user => 
    user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.user_id.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f5f5f5' }}>
      {/* Notification */}
      {notification && (
        <Notification
          type={notification.type}
          message={notification.message}
          onClose={() => setNotification(null)}
        />
      )}

      {/* Confirm Modal */}
      {confirmModal && (
        <ConfirmModal
          title={confirmModal.title}
          message={confirmModal.message}
          type={confirmModal.type}
          onConfirm={confirmModal.onConfirm}
          onCancel={confirmModal.onCancel}
        />
      )}

      {/* Edit User Modal */}
      {editingUser && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '2rem'
          }}
          onClick={() => setEditingUser(null)}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '16px',
              padding: '2rem',
              maxWidth: '520px',
              width: '100%',
              boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={{ fontSize: '1.6rem', marginBottom: '1.5rem' }}>Edit User</h2>
            <div style={{ marginBottom: '1rem', fontSize: '0.9rem', color: '#64748b' }}>
              <div><strong>User ID:</strong> {editingUser.user_id}</div>
              <div><strong>Stripe Customer:</strong> {editingUser.stripe_customer_id || '—'}</div>
              <div><strong>Stripe Subscription:</strong> {editingUser.stripe_subscription_id || '—'}</div>
            </div>
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', fontWeight: '600', marginBottom: '0.5rem' }}>Name</label>
              <input
                type="text"
                value={editForm.name}
                onChange={(e) => setEditForm((prev) => ({ ...prev, name: e.target.value }))}
                style={{
                  width: '100%',
                  padding: '0.85rem',
                  border: '2px solid #e2e8f0',
                  borderRadius: '8px',
                  fontSize: '1rem'
                }}
              />
            </div>
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', fontWeight: '600', marginBottom: '0.5rem' }}>Email</label>
              <input
                type="email"
                value={editForm.email}
                onChange={(e) => setEditForm((prev) => ({ ...prev, email: e.target.value }))}
                style={{
                  width: '100%',
                  padding: '0.85rem',
                  border: '2px solid #e2e8f0',
                  borderRadius: '8px',
                  fontSize: '1rem'
                }}
              />
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem' }}>
              <button
                onClick={() => setEditingUser(null)}
                style={{
                  padding: '0.75rem 1.5rem',
                  borderRadius: '8px',
                  border: '2px solid #e2e8f0',
                  backgroundColor: '#f8fafc',
                  cursor: 'pointer',
                  fontWeight: '600'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleUpdateUser}
                style={{
                  padding: '0.75rem 1.5rem',
                  borderRadius: '8px',
                  border: 'none',
                  backgroundColor: '#4A90E2',
                  color: 'white',
                  cursor: 'pointer',
                  fontWeight: '600'
                }}
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Admin Header */}
      <div style={{
        backgroundColor: '#2c3e50',
        color: 'white',
        padding: '1.5rem 2rem',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
      }}>
        <div style={{
          maxWidth: '1400px',
          margin: '0 auto',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '1rem'
        }}>
          <div>
            <h1 style={{ fontSize: '2rem', marginBottom: '0.25rem' }}>QuotePro Admin</h1>
            <p style={{ opacity: 0.8 }}>Welcome, {admin?.email}</p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <button
              onClick={handleBackToApp}
              style={{
                padding: '0.75rem 1.5rem',
                backgroundColor: '#4A90E2',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '1rem',
                fontWeight: '600',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
            >
              <ArrowLeft size={18} />
              Back to App
            </button>
            <button
              onClick={handleLogout}
              style={{
                padding: '0.75rem 1.5rem',
                backgroundColor: '#e74c3c',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '1rem',
                fontWeight: '600',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
            >
              <LogOut size={20} />
              Logout
            </button>
          </div>
        </div>
      </div>

      <div style={{ maxWidth: '1400px', margin: '2rem auto', padding: '0 2rem' }}>
        {/* Statistics Cards */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          gap: '1.5rem',
          marginBottom: '2rem'
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '1.5rem',
            borderRadius: '12px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '0.5rem' }}>
              <Users size={32} style={{ color: '#4A90E2' }} />
              <span style={{ fontSize: '0.9rem', color: '#666' }}>Total Users</span>
            </div>
            <p style={{ fontSize: '2.5rem', fontWeight: 'bold', color: '#333' }}>{stats?.total_users || 0}</p>
          </div>

          <div style={{
            backgroundColor: 'white',
            padding: '1.5rem',
            borderRadius: '12px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '0.5rem' }}>
              <UserCheck size={32} style={{ color: '#5FA75F' }} />
              <span style={{ fontSize: '0.9rem', color: '#666' }}>Active Subscriptions</span>
            </div>
            <p style={{ fontSize: '2.5rem', fontWeight: 'bold', color: '#5FA75F' }}>{stats?.active_subscriptions || 0}</p>
          </div>

          <div style={{
            backgroundColor: 'white',
            padding: '1.5rem',
            borderRadius: '12px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '0.5rem' }}>
              <UserX size={32} style={{ color: '#e74c3c' }} />
              <span style={{ fontSize: '0.9rem', color: '#666' }}>Inactive Subscriptions</span>
            </div>
            <p style={{ fontSize: '2.5rem', fontWeight: 'bold', color: '#e74c3c' }}>{stats?.inactive_subscriptions || 0}</p>
          </div>

          <div style={{
            backgroundColor: 'white',
            padding: '1.5rem',
            borderRadius: '12px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '0.5rem' }}>
              <ShieldOff size={32} style={{ color: '#f97316' }} />
              <span style={{ fontSize: '0.9rem', color: '#666' }}>Disabled Accounts</span>
            </div>
            <p style={{ fontSize: '2.5rem', fontWeight: 'bold', color: '#f97316' }}>{stats?.disabled_accounts || 0}</p>
          </div>

          <div style={{
            backgroundColor: 'white',
            padding: '1.5rem',
            borderRadius: '12px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '0.5rem' }}>
              <FileText size={32} style={{ color: '#f39c12' }} />
              <span style={{ fontSize: '0.9rem', color: '#666' }}>Total Quotes</span>
            </div>
            <p style={{ fontSize: '2.5rem', fontWeight: 'bold', color: '#333' }}>{stats?.total_quotes || 0}</p>
          </div>

          <div style={{
            backgroundColor: 'white',
            padding: '1.5rem',
            borderRadius: '12px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '0.5rem' }}>
              <DollarSign size={32} style={{ color: '#16a34a' }} />
              <span style={{ fontSize: '0.9rem', color: '#666' }}>Quote Amount (Total)</span>
            </div>
            <p style={{ fontSize: '2.25rem', fontWeight: 'bold', color: '#333' }}>
              {formatCurrency(stats?.total_quote_amount)}
            </p>
          </div>

          <div style={{
            backgroundColor: 'white',
            padding: '1.5rem',
            borderRadius: '12px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '0.5rem' }}>
              <DollarSign size={32} style={{ color: '#0ea5e9' }} />
              <span style={{ fontSize: '0.9rem', color: '#666' }}>Approved Amount</span>
            </div>
            <p style={{ fontSize: '2.25rem', fontWeight: 'bold', color: '#333' }}>
              {formatCurrency(stats?.approved_quote_amount)}
            </p>
            <p style={{ marginTop: '0.5rem', color: '#666', fontSize: '0.9rem' }}>
              Approved Quotes: {stats?.approved_quote_count || 0}
            </p>
          </div>

          <div style={{
            backgroundColor: 'white',
            padding: '1.5rem',
            borderRadius: '12px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '0.5rem' }}>
              <TrendingUp size={32} style={{ color: '#9b59b6' }} />
              <span style={{ fontSize: '0.9rem', color: '#666' }}>New Users (30d)</span>
            </div>
            <p style={{ fontSize: '2.5rem', fontWeight: 'bold', color: '#333' }}>{stats?.recent_registrations || 0}</p>
          </div>
        </div>

        {/* Invite Management Section */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '2rem',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          marginBottom: '2rem'
        }}>
          <div style={{ marginBottom: '1.5rem' }}>
            <h2 style={{ fontSize: '1.8rem', marginBottom: '0.5rem' }}>Invite Access</h2>
            <p style={{ color: '#64748b', margin: 0 }}>Invite users to a 15‑day free trial. Invites expire in 3 days.</p>
          </div>

          <form onSubmit={handleSendInvite} style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '1.5rem' }}>
            <input
              type="email"
              placeholder="customer@email.com"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              style={{
                flex: 1,
                minWidth: '240px',
                padding: '0.85rem 1rem',
                borderRadius: '10px',
                border: '2px solid #e2e8f0',
                fontSize: '1rem'
              }}
            />
            <button
              type="submit"
              disabled={sendingInvite}
              style={{
                padding: '0.85rem 1.5rem',
                borderRadius: '10px',
                border: 'none',
                backgroundColor: '#4A90E2',
                color: 'white',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              {sendingInvite ? 'Sending...' : 'Send Invite'}
            </button>
          </form>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {invites.length === 0 ? (
              <p style={{ color: '#64748b' }}>No invites sent yet.</p>
            ) : invites.map((invite) => {
              const trialLabel = invite.trial_days_left != null
                ? `${invite.trial_days_left} days left`
                : '—';
              const statusColors = {
                pending: { bg: '#e0f2fe', text: '#0369a1' },
                used: { bg: '#dcfce7', text: '#166534' },
                expired: { bg: '#fee2e2', text: '#991b1b' }
              };
              const statusStyle = statusColors[invite.status] || statusColors.pending;
              return (
                <div
                  key={invite.invite_id || invite.token}
                  style={{
                    border: '1px solid #e5e7eb',
                    borderRadius: '12px',
                    padding: '1rem 1.25rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    gap: '1rem'
                  }}
                >
                  <div>
                    <div style={{ fontWeight: '600' }}>{invite.email}</div>
                    <div style={{ color: '#64748b', fontSize: '0.85rem' }}>
                      Invited: {formatDate(invite.created_at)} • Expires: {formatDate(invite.expires_at)}
                    </div>
                    <div style={{ color: '#64748b', fontSize: '0.85rem' }}>
                      Subscription: {invite.subscription_status || '—'} • Trial: {trialLabel}
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
                    <span style={{
                      padding: '0.3rem 0.75rem',
                      borderRadius: '999px',
                      fontSize: '0.75rem',
                      fontWeight: '600',
                      backgroundColor: statusStyle.bg,
                      color: statusStyle.text
                    }}>
                      {invite.status}
                    </span>
                    {invite.invite_link && (
                      <button
                        type="button"
                        onClick={() => handleCopyInviteLink(invite.invite_link)}
                        style={{
                          padding: '0.45rem 0.75rem',
                          borderRadius: '8px',
                          border: '1px solid #cbd5f5',
                          backgroundColor: '#f8fafc',
                          color: '#1f2937',
                          cursor: 'pointer',
                          fontSize: '0.85rem'
                        }}
                      >
                        Copy Link
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => handleDeleteInvite(invite)}
                      style={{
                        width: '36px',
                        height: '36px',
                        borderRadius: '10px',
                        border: '1px solid #fecaca',
                        backgroundColor: '#fee2e2',
                        color: '#b91c1c',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer'
                      }}
                      aria-label={`Delete invite for ${invite.email}`}
                      title="Delete invite"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* User Management Section */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '2rem',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
        }}>
          <div style={{ marginBottom: '1.5rem' }}>
            <h2 style={{ fontSize: '1.8rem', marginBottom: '1rem' }}>User Management</h2>
            <div style={{ position: 'relative' }}>
              <Search size={20} style={{
                position: 'absolute',
                left: '1rem',
                top: '50%',
                transform: 'translateY(-50%)',
                color: '#999'
              }} />
              <input
                type="text"
                placeholder="Search users by name or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  width: '100%',
                  padding: '1rem 1rem 1rem 3rem',
                  fontSize: '1rem',
                  border: '2px solid #ddd',
                  borderRadius: '8px'
                }}
              />
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {filteredUsers.map((user) => {
              const isExpanded = expandedUserId === user.user_id;
              const isTrial = user.subscription_status === 'trial';
              const trialDaysLeft = typeof user.trial_days_left === 'number' ? user.trial_days_left : null;
              const subscriptionLabel = isTrial
                ? `Trial${trialDaysLeft !== null ? ` • ${trialDaysLeft}d` : ''}`
                : (user.subscription_status === 'active' ? 'Sub Active' : 'Sub Inactive');
              const subscriptionColors = isTrial
                ? { bg: '#e0f2fe', text: '#0369a1' }
                : (user.subscription_status === 'active'
                    ? { bg: '#d4edda', text: '#155724' }
                    : { bg: '#f8d7da', text: '#721c24' });
              return (
                <div
                  key={user.user_id}
                  style={{
                    border: '1px solid #e5e7eb',
                    borderRadius: '12px',
                    padding: '1rem 1.25rem',
                    backgroundColor: '#fff',
                    boxShadow: isExpanded ? '0 8px 20px rgba(15, 23, 42, 0.08)' : 'none',
                    cursor: 'pointer'
                  }}
                  onClick={() => setExpandedUserId(isExpanded ? null : user.user_id)}
                >
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', minWidth: '260px' }}>
                      {user.picture ? (
                        <img src={user.picture} alt={user.name} style={{ width: '44px', height: '44px', borderRadius: '50%' }} />
                      ) : (
                        <div style={{
                          width: '44px',
                          height: '44px',
                          borderRadius: '50%',
                          backgroundColor: '#4A90E2',
                          color: 'white',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '1.15rem',
                          fontWeight: 'bold'
                        }}>
                          {user.name.charAt(0).toUpperCase()}
                        </div>
                      )}
                      <div>
                        <div style={{ fontWeight: '600' }}>{user.name}</div>
                        <div style={{ color: '#64748b', fontSize: '0.85rem' }}>{user.email}</div>
                      </div>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
                      <span style={{
                        padding: '0.35rem 0.9rem',
                        borderRadius: '999px',
                        fontSize: '0.8rem',
                        fontWeight: '600',
                        backgroundColor: user.account_status === 'active' ? '#d4edda' : '#f8d7da',
                        color: user.account_status === 'active' ? '#155724' : '#721c24'
                      }}>
                        {user.account_status === 'active' ? 'Active' : 'Disabled'}
                      </span>
                      <span style={{
                        padding: '0.35rem 0.9rem',
                        borderRadius: '999px',
                        fontSize: '0.8rem',
                        fontWeight: '600',
                        backgroundColor: subscriptionColors.bg,
                        color: subscriptionColors.text
                      }}>
                        {subscriptionLabel}
                      </span>
                      <span style={{ fontSize: '0.85rem', color: '#64748b' }}>
                        Quotes: <strong style={{ color: '#334155' }}>{user.quote_count || 0}</strong>
                      </span>
                      <span style={{ fontSize: '0.85rem', color: '#64748b' }}>
                        Last login: <strong style={{ color: '#334155' }}>{formatDate(user.last_login)}</strong>
                      </span>
                    </div>
                  </div>

                  {isExpanded && (
                    <div
                      onClick={(event) => event.stopPropagation()}
                      style={{
                        marginTop: '1rem',
                        borderTop: '1px solid #e5e7eb',
                        paddingTop: '1rem',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '0.75rem'
                      }}
                    >
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1.5rem', color: '#475569', fontSize: '0.9rem' }}>
                        <div><strong>User ID:</strong> {user.user_id}</div>
                        <div><strong>Auth:</strong> {user.auth_provider || '—'}</div>
                        <div><strong>Stripe Status:</strong> {user.stripe_subscription_status || '—'}</div>
                        <div><strong>Subscription Ends:</strong> {formatDate(user.subscription_end_date)}</div>
                        <div><strong>Trial Ends:</strong> {formatDate(user.trial_end_date)}</div>
                        <div><strong>Last Reset:</strong> {formatDate(user.last_password_reset)}</div>
                        <div><strong>Joined:</strong> {formatDate(user.created_at)}</div>
                      </div>

                      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                        <button
                          onClick={() => handleEditUser(user)}
                          style={{
                            padding: '0.5rem 0.85rem',
                            backgroundColor: '#4A90E2',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '0.85rem',
                            fontWeight: '600',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.35rem'
                          }}
                        >
                          <Edit size={14} />
                          Edit
                        </button>
                        <button
                          onClick={() => handleSendResetEmail(user)}
                          style={{
                            padding: '0.5rem 0.85rem',
                            backgroundColor: '#0ea5e9',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '0.85rem',
                            fontWeight: '600',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.35rem'
                          }}
                        >
                          <Mail size={14} />
                          Reset Email
                        </button>
                        <button
                          onClick={() => handleRevokeSessions(user)}
                          style={{
                            padding: '0.5rem 0.85rem',
                            backgroundColor: '#64748b',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '0.85rem',
                            fontWeight: '600',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.35rem'
                          }}
                        >
                          <RefreshCcw size={14} />
                          Revoke
                        </button>
                        <button
                          onClick={() => handleToggleAccountStatus(user)}
                          style={{
                            padding: '0.5rem 0.85rem',
                            backgroundColor: user.account_status === 'active' ? '#f97316' : '#5FA75F',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '0.85rem',
                            fontWeight: '600',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.35rem'
                          }}
                        >
                          <ShieldOff size={14} />
                          {user.account_status === 'active' ? 'Disable' : 'Enable'}
                        </button>
                        <button
                          onClick={() => handleToggleSubscription(user.user_id, user.subscription_status)}
                          style={{
                            padding: '0.5rem 0.85rem',
                            backgroundColor: user.subscription_status === 'active' ? '#e74c3c' : '#5FA75F',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '0.85rem',
                            fontWeight: '600'
                          }}
                        >
                          {user.subscription_status === 'active' ? 'Deactivate Sub' : 'Activate Sub'}
                        </button>
                        <button
                          onClick={() => handleCancelSubscription(user)}
                          style={{
                            padding: '0.5rem 0.85rem',
                            backgroundColor: '#dc2626',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '0.85rem',
                            fontWeight: '600'
                          }}
                        >
                          Cancel Stripe
                        </button>
                        <button
                          onClick={() => handleDeleteUser(user.user_id, user.name)}
                          style={{
                            padding: '0.5rem 0.85rem',
                            backgroundColor: '#b91c1c',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '0.85rem',
                            fontWeight: '600'
                          }}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

      </div>
    </div>
  );
};

export default AdminDashboard;
